import React, { useEffect, useState } from "react";
import {
  Button,
  message,
  Modal,
  Form,
  Input,
  Table,
  Popconfirm,
  Select,
} from "antd";
import { Trash2, Edit } from "lucide-react";
import { useCart } from "../../../../Context";
import {
  useRestaurantTablesQuery,
  useActiveStoresQuery,
  useCreateRestaurantTableMutation,
  useUpdateRestaurantTableMutation,
  useDeleteRestaurantTableMutation,
} from "../../../../Services";
import { modalTitle } from "../../../../components";

interface TableModalProps {
  open: boolean;
  onCancel: () => void;
}

export const TableModal: React.FC<TableModalProps> = ({ open, onCancel }) => {
  const [form] = Form.useForm();
  const [editingTableId, setEditingTableId] = useState<string | null>(null);
  const { table, setTable, clearCart } = useCart();

   const {
  data: tableResponse,
  isLoading: loadingTables,
  refetch: refetchTables,
} = useRestaurantTablesQuery({
  status: "active",
});

const tables = tableResponse?.data ?? [];

  const { data: activeStores, isLoading: loadingStores } =
    useActiveStoresQuery();

  // Mutations
  const createMutation = useCreateRestaurantTableMutation();
  const updateMutation = useUpdateRestaurantTableMutation();
  const deleteMutation = useDeleteRestaurantTableMutation();

  const handleFinish = async (values: any) => {
    if (editingTableId) {
      updateMutation.mutate(
        { id: editingTableId, ...values },
        {
          onSuccess: (res) => {
            if (res?.success) {
              message.success("Table updated successfully.");
              if (table?.id === editingTableId) {
                setTable({ id: editingTableId!, name: values.name });
              }
              setEditingTableId(null);
              form.resetFields();
            } else {
              message.error(res?.message || "Failed to update table.");
            }
          },
          onError: () => {
            message.error("Failed to update table.");
          },
        },
      );
    } else {
      createMutation.mutate(values, {
        onSuccess: (res) => {
          if (res?.success) {
            message.success("Table created successfully.");
            setTable({ id: res.id, name: values.name });
            form.resetFields();
          } else {
            message.error(res?.message || "Failed to create table.");
          }
        },
        onError: () => {
          message.error("Failed to create table.");
        },
      });
    }
  };

  const handleDelete = async (id: string) => {
    deleteMutation.mutate(
      { id },
      {
        onSuccess: (res) => {
          if (res?.success) {
            message.success("Table deleted successfully.");
            if (table?.id === id) {
              setTable(null);
            }
          } else {
            message.error(res?.message || "Failed to delete table.");
          }
        },
        onError: () => {
          message.error("Failed to delete table.");
        },
      },
    );
  };

  const columns = [
    {
      title: "Table Name",
      dataIndex: "name",
      key: "name",
      className: "font-semibold text-gray-800",
    },
    {
      title: "Store",
      dataIndex: "store_name",
      key: "store_name",
      className: "text-gray-600 font-medium",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      className: "text-gray-500",
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: any, record: any) => (
        <div className="flex gap-2">
          <Button
            size="small"
            type="text"
            icon={<Edit className="w-4 h-4 text-blue-600" />}
            onClick={() => {
              setEditingTableId(record.id);
              form.setFieldsValue({
                name: record.name,
                description: record.description,
                store_id: record.store_id,
              });
            }}
          />
          <Popconfirm
            title="Are you sure you want to delete this table?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
          >
            <Button
              size="small"
              type="text"
              danger
              icon={<Trash2 className="w-4 h-4" />}
            />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={modalTitle(editingTableId ? "Edit Table" : "Manage Tables")}
      open={open}
      onCancel={() => {
        setEditingTableId(null);
        form.resetFields();
        onCancel();
      }}
      footer={null}
      destroyOnClose
      width={650}
    >
      <div className="flex flex-col gap-6 pt-2">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          className="bg-gray-50/50 p-4 rounded-lg border border-gray-100"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Form.Item
              name="name"
              label={
                <span className="text-xs font-semibold text-gray-600">
                  Table Name / Number
                </span>
              }
              rules={[{ required: true, message: "Please enter table name." }]}
              className="mb-2"
            >
              <Input placeholder="e.g. Table 1 or VIP Room" size="middle" />
            </Form.Item>

            <Form.Item
              name="store_id"
              label={
                <span className="text-xs font-semibold text-gray-600">
                  Store
                </span>
              }
              rules={[{ required: true, message: "Please select store." }]}
              className="mb-2"
            >
              <Select
                placeholder="Select Store"
                size="middle"
                loading={loadingStores}
                options={activeStores?.map((s: any) => ({
                  value: s.id,
                  label: s.name,
                }))}
              />
            </Form.Item>

            <Form.Item
              name="description"
              label={
                <span className="text-xs font-semibold text-gray-600">
                  Description (Optional)
                </span>
              }
              className="mb-2"
            >
              <Input placeholder="e.g. Near window, 4 seats" size="middle" />
            </Form.Item>
          </div>

          <div className="flex justify-end gap-2 pt-3 mt-2 border-t border-gray-100">
            {editingTableId && (
              <Button
                size="middle"
                onClick={() => {
                  setEditingTableId(null);
                  form.resetFields();
                }}
                disabled={updateMutation.isPending}
              >
                Cancel Edit
              </Button>
            )}
            <Button
              size="middle"
              type="primary"
              htmlType="submit"
              loading={
                editingTableId
                  ? updateMutation.isPending
                  : createMutation.isPending
              }
            >
              {editingTableId ? "Update Table" : "Add Table"}
            </Button>
          </div>
        </Form>

        <div className="flex flex-col gap-2">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Existing Tables ({tables?.length})
          </span>
          <Table
            dataSource={tables}
            columns={columns}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 5, size: "small", showSizeChanger: false }}
            loading={loadingTables}
            className="border border-gray-100 rounded-lg overflow-hidden"
          />
        </div>
      </div>
    </Modal>
  );
};
